import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import NotificationModal from "../../components/NotificationModal.js";
import useNotification from "../../hooks/useNotification.js";
import { CATEGORIES } from "../../utils/calculateTotal.js";
import useAutoRefresh from "../../hooks/useAutoRefresh.js";
import { useAdmin } from "../../context/AdminContext.js";
import Spinner from "../../components/Spinner.js";
import Pagination from "../../components/Pagination.js";
import InvoiceEditModal from "../../components/admin/InvoiceEditModal.js";
import "./Admin.css";

const STATUSES = ["pending", "approved", "in_progress", "completed", "cancelled"];
const PER_PAGE = 10;

const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || key;
const formatCategories = (categories) => (Array.isArray(categories) ? categories : [categories]).map(categoryLabel).join(", ");

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { notification, notify, dismiss } = useNotification();
  const [savingId, setSavingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const { admin } = useAdmin();

  // silent=true (background auto-refresh) skips the loading/error state changes so a live poll
  // never flashes "Loading…" over an already-rendered table or clobbers it with an error banner
  // over a transient network blip.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const { data } = await apiClient.get("/bookings", { params: { status, q, page, perPage: PER_PAGE } });
      setBookings(data.data);
      setTotal(data.total);
    } catch (err) {
      if (!silent) setError("Could not load bookings.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [status, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Keeps this page live without a manual reload: refetches when the tab regains focus and on a
  // background interval, so an admin who leaves it open doesn't keep looking at a stale snapshot.
  useAutoRefresh(useCallback(() => load(true), [load]));

  const handleStatusChange = async (id, newStatus) => {
    setSavingId(id);
    try {
      await apiClient.post(`/bookings/${id}/status`, { status: newStatus });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: newStatus } : b)));
      notify("success", "Status updated", `Booking marked as ${newStatus.replace(/_/g, " ")}.`);
    } catch (err) {
      notify("error", "Could not update status", "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleApprove = async (id) => {
    setSavingId(id);
    try {
      const { data } = await apiClient.post(`/bookings/${id}/approve`);
      setBookings((prev) =>
        prev.map((b) =>
          b.id === id
            ? { ...b, status: "approved", invoice_id: data.invoice.id, invoice_number: data.invoice.invoiceNumber }
            : b
        )
      );
      notify(
        data.warning ? "warning" : "success",
        "Booking approved",
        data.warning || "Invoice generated and confirmation email sent."
      );
    } catch (err) {
      notify("error", "Could not approve this booking", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleResendEmail = async (id) => {
    setSavingId(id);
    try {
      await apiClient.post(`/bookings/${id}/resend-invoice-email`);
      notify("success", "Email sent", "Confirmation email resent.");
    } catch (err) {
      notify("error", "Could not send the email", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleInvoiceSaved = () => {
    setEditingBooking(null);
    notify("success", "Invoice updated", "Quantities and totals have been recalculated and the invoice regenerated.");
    load();
  };

  const handleRegenerateInvoice = async (invoiceId) => {
    setSavingId(invoiceId);
    try {
      await apiClient.post(`/invoices/${invoiceId}/regenerate`);
      notify("success", "Invoice regenerated", "PDF regenerated successfully.");
      load();
    } catch (err) {
      notify("error", "Could not regenerate invoice", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleMarkPaid = async (id) => {
    setSavingId(id);
    try {
      await apiClient.post(`/bookings/${id}/payment-status`, { paymentStatus: "paid" });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, payment_status: "paid" } : b)));
      notify("success", "Payment recorded", "Booking marked as paid.");
    } catch (err) {
      notify("error", "Could not update payment status", "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    setDownloadingId(invoiceId);
    try {
      const { data } = await apiClient.get(`/invoices/${invoiceId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "invoice.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify("error", "Could not download invoice", "Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Bookings</h1>
        <p>{total} total</p>
      </div>

      <div className="admin-filters">
        <input
          type="search"
          placeholder="Search by name, email or phone…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="admin-error">{error}</p>}

      {loading ? (
        <Spinner label="Loading bookings…" />
      ) : bookings.length === 0 ? (
        <p>No bookings found.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>S.N.</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Service</th>
                <th>Schedule</th>
                <th>Payment</th>
                <th>Total</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => (
                <tr key={booking.id}>
                  <td>{(page - 1) * PER_PAGE + index + 1}</td>
                  <td className="admin-ellipsis">{booking.full_name}</td>
                  <td className="admin-details-cell">
                    <div className="admin-ellipsis">{booking.email}</div>
                    <div className="admin-subtle">{booking.phone}</div>
                  </td>
                  <td className="admin-ellipsis">{formatCategories(booking.service_type)}</td>
                  <td>
                    {booking.service_date} {booking.service_time}
                  </td>
                  <td>
                    {booking.payment_method}
                    <div className="admin-subtle">{booking.payment_status.replace(/_/g, " ")}</div>
                    {booking.payment_status === "awaiting_instructions" && (
                      <button
                        type="button"
                        className="admin-mark-paid-btn"
                        disabled={savingId === booking.id}
                        onClick={() => handleMarkPaid(booking.id)}
                      >
                        {savingId === booking.id ? (
                          <>
                            <Spinner size="sm" inline /> Saving…
                          </>
                        ) : (
                          "Mark as paid"
                        )}
                      </button>
                    )}
                  </td>
                  <td>${booking.quoted_total}</td>
                  <td>{formatDateTime(booking.created_at)}</td>
                  <td className="admin-status-cell">
                    <div className="admin-cell-flex">
                      <select
                        value={booking.status}
                        disabled={savingId === booking.id}
                        onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} disabled={s === "approved"}>
                            {s.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>

                      {booking.status === "pending" && (
                        <button
                          type="button"
                          className="admin-approve-btn"
                          disabled={savingId === booking.id}
                          onClick={() => handleApprove(booking.id)}
                        >
                          {savingId === booking.id ? (
                            <>
                              <Spinner size="sm" inline /> Approving…
                            </>
                          ) : (
                            "Approve & Invoice"
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="admin-actions-cell">
                    <div className="admin-cell-flex">
                      {booking.invoice_id && (
                        <>
                          <button
                            type="button"
                            className="admin-invoice-link"
                            disabled={downloadingId === booking.invoice_id}
                            onClick={() => handleDownloadInvoice(booking.invoice_id)}
                          >
                            {downloadingId === booking.invoice_id ? (
                              <>
                                <Spinner size="sm" inline /> Downloading…
                              </>
                            ) : (
                              booking.invoice_number || "Download invoice"
                            )}
                          </button>
                          <button
                            type="button"
                            className="admin-resend-link"
                            disabled={savingId === booking.id}
                            onClick={() => handleResendEmail(booking.id)}
                          >
                            {savingId === booking.id ? (
                              <>
                                <Spinner size="sm" inline /> Sending…
                              </>
                            ) : (
                              "Resend email"
                            )}
                          </button>

                          {admin && (admin.role === "admin" || admin.role === "super_admin") && (
                            <>
                              <button
                                type="button"
                                className="admin-edit-link"
                                onClick={() => setEditingBooking(booking)}
                              >
                                Edit invoice
                              </button>
                              <button
                                type="button"
                                className="admin-regenerate-link"
                                disabled={savingId === booking.invoice_id}
                                onClick={() => handleRegenerateInvoice(booking.invoice_id)}
                              >
                                {savingId === booking.invoice_id ? (
                                  <>
                                    <Spinner size="sm" inline /> Regenerating…
                                  </>
                                ) : (
                                  "Regenerate"
                                )}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} perPage={PER_PAGE} total={total} onPageChange={setPage} />
        </div>
      )}

      {editingBooking && (
        <InvoiceEditModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={handleInvoiceSaved}
        />
      )}

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default BookingsPage;
